import React, { useState, useEffect, useRef } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Container,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Form,
    FormGroup,
    Input,
    Label,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Spinner,
    InputGroup,
    InputGroupText,
    Col,
    Row
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, MoreVertical, Trash, Download } from "lucide-react";
import Select from 'react-select';
import ReactQuill from "react-quill";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";


const ClientsList = () => {
    const [clients, setClients] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [whatsapp, setWhatsapp] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [profession, setProfession] = useState("");
    const [remarks, setRemarks] = useState("");
    const [status, setStatus] = useState("active");
    const [address, setAddress] = useState("");
    const [website, setWebsite] = useState("");
    const [client, setClient] = useState("");
    const [socialMedia, setSocial] = useState("");
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientToDelete, setClientToDelete] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [importModal, setImportModal] = useState(false);
    const [file, setFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);

    // Location states
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);

    const [exportModal, setExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('excel');
    const [exportLoading, setExportLoading] = useState(false);

    const toggleDropdown = (id) => setDropdownOpen(dropdownOpen === id ? null : id);
    const toast = useRef(null);

    const toggle = () => setModal(!modal);
    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const breadcrumbItems = [
        { title: "Clients", link: "/" },
        { title: "Clients", link: "/admin/clients" },
    ];

    const statusOptions = [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
        { label: "Suspended", value: "suspended" },
        { label: "VIP", value: "vip" }
    ];

    const fetchClients = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/clients/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            console.log("API Response:", response.data);

            // Check what type of data we're getting
            console.log("Response data type:", typeof response.data);

            // Make sure we're setting an array to clients state
            let clientsData;
            if (Array.isArray(response.data)) {
                clientsData = response.data;
            } else if (response.data && Array.isArray(response.data.data)) {
                clientsData = response.data.data;
            } else if (response.data && typeof response.data === 'object') {
                // If it's an object but not properly structured, try to extract values
                clientsData = Object.values(response.data);
                if (!Array.isArray(clientsData[0])) {
                    // If first value isn't an array, flatten if needed
                    clientsData = clientsData.flat().filter(item => item && typeof item === 'object');
                }
            } else {
                // Fallback to empty array if nothing works
                clientsData = [];
            }

            console.log("Processed clients data:", clientsData);
            console.log("Is array:", Array.isArray(clientsData));

            setClients(Array.isArray(clientsData) ? clientsData : []);
            setTotalRecords(Array.isArray(clientsData) ? clientsData.length : 0);
        } catch (error) {
            console.error("Error fetching clients:", error);
            setClients([]);
            setTotalRecords(0);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to fetch clients',
                life: 3000
            });
        }
        setLoading(false);
    }, []);

    const fetchCountries = React.useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/countries`);
            setCountries(response.data.data || []);
        } catch (error) {
            console.error("Error fetching countries:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch countries',
                life: 3000
            });
        }
    }, []);

    const fetchStates = async (countryId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/states/${countryId}`);
            setStates(response.data.data || []);
        } catch (error) {
            console.error("Error fetching states:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch states',
                life: 3000
            });
        }
    };

    const fetchCities = async (stateId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/cities/${stateId}`);
            setCities(response.data.data || []);
        } catch (error) {
            console.error("Error fetching cities:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch cities',
                life: 3000
            });
        }
    };

    useEffect(() => {
        fetchClients();
        fetchCountries();
    }, [fetchClients, fetchCountries]);

    const nameBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="fw-bold">{rowData.name}</div>
                <div className="text-muted small">{rowData.email}</div>
                <div className="text-muted small">{rowData.phone}</div>
            </div>
        );
    };

    const locationBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="text-muted small">
                    {rowData.country || 'N/A'} {rowData.state ? `, ${rowData.state}` : ''} {rowData.city ? `, ${rowData.city}` : ''}
                </div>
                {rowData.address && <div className="text-muted small">{rowData.address}</div>}
            </div>
        );
    };

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = {
            name,
            email,
            phone,
            whatsapp,
            company_name: companyName,
            country_id: selectedCountry?.value || '',
            state_id: selectedState?.value || '',
            city_id: selectedCity?.value || '',
            address,
            profession,
            remarks,
            status,
            website,
            client,
            social_media: socialMedia,
            added_by: localStorage.getItem("user_id"),
        };

        setLoading(true);
        try {
            if (selectedClient) {
                await axios.post(`${API_BASE_URL}/clients/update/${selectedClient.id}`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({ severity: 'success', summary: 'Updated', detail: 'Client updated successfully', life: 3000 });
            } else {
                await axios.post(`${API_BASE_URL}/clients/add`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({ severity: 'success', summary: 'Added', detail: 'Client added successfully', life: 3000 });
            }
            resetForm();
            toggle();
            fetchClients();
        } catch (error) {
            console.error("Error details:", error.response?.data);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to save client',
                life: 3000
            });
        }
        setLoading(false);
    };

    const resetForm = () => {
        setName("");
        setEmail("");
        setPhone("");
        setWhatsapp("");
        setCompanyName("");
        setProfession("");
        setRemarks("");
        setAddress("");
        setStatus("active");
        setSelectedClient(null);
        setSelectedCountry(null);
        setSelectedState(null);
        setSelectedCity(null);
        setWebsite(null);
        setClient(null);
        setSocial(null);
        setStates([]);
        setCities([]);
    };

    const handleDelete = async () => {
        if (!clientToDelete) return;
        try {
            await axios.delete(`${API_BASE_URL}/clients/delete/${clientToDelete.id}`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            toast.current?.show({
                severity: "success",
                summary: "Deleted",
                detail: "Client deleted successfully",
                life: 3000
            });
            fetchClients();
        } catch (error) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to delete client",
                life: 3000
            });
        }
        setClientToDelete(null);
        toggleDeleteModal();
    };

    const statusBodyTemplate = (rowData) => {
        const getStatusClass = (status) => {
            switch (status) {
                case 'active': return 'success';
                case 'inactive': return 'secondary';
                case 'suspended': return 'warning';
                case 'vip': return 'primary';
                default: return 'light';
            }
        };

        const statusLabel = statusOptions.find(opt => opt.value === rowData.status)?.label || rowData.status;
        const statusClass = getStatusClass(rowData.status);

        return (
            <span className={`badge bg-${statusClass}-subtle text-${statusClass} text-uppercase`}>
                {statusLabel}
            </span>
        );
    };

    const emptyFieldTemplate = (field) => {
        return field ? field : <span className="text-muted">N/A</span>;
    };

    const handleImport = async () => {
        if (!file) return;

        setImportLoading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${API_BASE_URL}/clients/import`, formData, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem("token"),
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: response.data.message || 'Clients imported successfully',
                life: 3000
            });

            fetchClients();
            setImportModal(false);
            setFile(null);
        } catch (error) {
            console.error("Import error:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to import clients',
                life: 3000
            });
        } finally {
            setImportLoading(false);
        }
    };

    const handleExport = async () => {
        setExportLoading(true);

        try {
            // Prepare data for export
            const prepareExportData = (data) => {
                return data.map(client => ({
                    'Name': client.name || '',
                    'Email': client.email || '',
                    'Phone': client.phone || '',
                    'WhatsApp': client.whatsapp || '',
                    'Company': client.company_name || '',
                    'Profession': client.profession || '',
                    'Status': statusOptions.find(opt => opt.value === client.status)?.label || client.status || '',
                    'Country': client.country || '',
                    'State': client.state || '',
                    'City': client.city || '',
                    'Address': client.address || '',
                    'Website': client.website || '',
                    'About Client': client.about_client || '',
                    'Remarks': client.remarks || '',
                    'Created Date': new Date(client.created_at).toLocaleDateString(),
                    'Last Updated': new Date(client.updated_at).toLocaleDateString()
                }));
            };

            const dataToExport = prepareExportData(clients);

            if (exportFormat === 'excel') {
                exportToExcel(dataToExport);
            } else if (exportFormat === 'csv') {
                exportToCSV(dataToExport);
            } else if (exportFormat === 'pdf') {
                exportToPDF(dataToExport);
            }

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: `Clients exported as ${exportFormat.toUpperCase()} successfully`,
                life: 3000
            });
        } catch (error) {
            console.error("Export error:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to export clients',
                life: 3000
            });
        } finally {
            setExportLoading(false);
            setExportModal(false);
        }
    };

    const exportToExcel = (data) => {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");
        XLSX.writeFile(workbook, `clients_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const exportToCSV = (data) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `clients_export_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportToPDF = (data) => {
        const doc = new jsPDF('landscape');
        const date = new Date().toLocaleDateString();
        const time = new Date().toLocaleTimeString();

        // Add title and metadata
        doc.setFontSize(18);
        doc.text("Client Management Report", 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on ${date} at ${time}`, 14, 28);

        // Add summary statistics
        let yPos = 36;
        doc.setFont(undefined, 'bold');
        doc.text("Summary Statistics", 14, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 6;

        doc.text(`Total Clients: ${data.length}`, 20, yPos);
        yPos += 6;

        // Add client details table
        doc.setFont(undefined, 'bold');
        doc.text("Client Details", 14, yPos);
        yPos += 8;

        doc.autoTable({
            startY: yPos,
            head: [['Name', 'Email', 'Phone', 'Country', 'State', 'City', 'Address', 'Website', 'Status']],
            body: data.map(client => [
                client.Name,
                client.Email,
                client.Phone,
                client.Country,
                client.State,
                client.City,
                client.Address,
                client.Website,
                client.Status
            ]),
            theme: 'striped',
            headStyles: { fillColor: [41, 128, 185] },
            columnStyles: {
                0: { cellWidth: 25 },
                1: { cellWidth: 30 },
                2: { cellWidth: 20 },
                3: { cellWidth: 25 },
                4: { cellWidth: 25 },
                5: { cellWidth: 25 },
                6: { cellWidth: 30 },
                7: { cellWidth: 30 },
                8: { cellWidth: 'auto' }
            },
            margin: { top: 25, right: 10, left: 10 },
            didDrawPage: function (data) {
                // Add page number at the bottom
                doc.setFontSize(8);
                doc.text(
                    'Page ' + doc.internal.getCurrentPageInfo().pageNumber,
                    data.settings.margin.left,
                    doc.internal.pageSize.height - 10
                );
            }
        });

        // Save the PDF
        doc.save(`client_report_${new Date().toISOString().slice(0, 10)}.pdf`);
    };


    const editClient = (client) => {
        setSelectedClient(client);
        setName(client.name);
        setEmail(client.email);
        setPhone(client.phone);
        setWhatsapp(client.whatsapp);
        setCompanyName(client.company_name);
        setProfession(client.profession);
        setRemarks(client.remarks);
        setAddress(client.address);
        setStatus(client.status);
        setWebsite(client.website);
        setClient(client.about_client);

        // Set location fields
        if (client.country_id) {
            const country = countries.find(c => c.id === client.country_id);
            if (country) {
                setSelectedCountry({ value: country.id, label: country.name });
                fetchStates(country.id).then(() => {
                    if (client.state_id) {
                        const state = states.find(s => s.id === client.state_id);
                        if (state) {
                            setSelectedState({ value: state.id, label: state.name });
                            fetchCities(state.id).then(() => {
                                if (client.city_id) {
                                    const city = cities.find(c => c.id === client.city_id);
                                    if (city) {
                                        setSelectedCity({ value: city.id, label: city.name });
                                    }
                                }
                            });
                        }
                    }
                });
            }
        }
        toggle();
    };

    useEffect(() => {
        console.log("Clients state:", clients);
    }, [clients]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Clients" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0">Clients List</h4>
                            {/* Mobile responsive row for search and add button */}
                            <Row className="mt-2 d-md-none">
                                <Col xs={4}>
                                    <InputGroup>
                                        <InputGroupText>
                                            <i className="pi pi-search" />
                                        </InputGroupText>
                                        <Input
                                            type="search"
                                            value={globalFilterValue}
                                            onChange={onGlobalFilterChange}
                                            placeholder="Search clients..."
                                        />
                                    </InputGroup>
                                </Col>
                                <Col xs={4} className="text-end">
                                    <Button color="primary" onClick={toggle} className="w-100">
                                        <i className="mdi mdi-plus mr-1"></i>Add
                                    </Button>
                                </Col>
                                <Col xs={4} className="text-end">
                                    <Button color="info" onClick={() => setExportModal(true)}>
                                        <Download size={14} className="me-1" />Export Clients
                                    </Button>
                                </Col>
                            </Row>

                            {/* Desktop version - remains unchanged */}
                            <div className="d-none d-md-flex align-items-center gap-3">
                                <InputGroup style={{ width: '300px' }}>
                                    <InputGroupText>
                                        <i className="pi pi-search" />
                                    </InputGroupText>
                                    <Input
                                        type="search"
                                        value={globalFilterValue}
                                        onChange={onGlobalFilterChange}
                                        placeholder="Search clients..."
                                    />
                                </InputGroup>
                                <Button color="primary" onClick={toggle}>
                                    <i className="mdi mdi-plus mr-1"></i>Add Client
                                </Button>
                                <Button color="info" onClick={() => setExportModal(true)}>
                                    <Download size={14} className="me-1" />Export Clients
                                </Button>
                            </div>
                        </CardHeader>
                        <CardBody>
                            <DataTable
                                value={clients}
                                loading={loading}
                                stripedRows
                                paginator
                                rows={rows}
                                first={first}
                                totalRecords={totalRecords}
                                onPage={onPageChange}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                filters={filters}
                                globalFilterFields={['name', 'email', 'company_name', 'status']}
                                emptyMessage="No clients found"
                            >
                                <Column header="SI.No" body={(rowData, { rowIndex }) => rowIndex + 1} />
                                <Column
                                    header="Clients"
                                    body={nameBodyTemplate}
                                    sortable
                                    sortField="name"
                                    filterPlaceholder="Search by name"
                                />
                                <Column header="Company" field="company_name" sortable body={(rowData) => emptyFieldTemplate(rowData.company_name)} />
                                <Column header="Location" sortable field="location" body={locationBodyTemplate} />
                                <Column header="Profession" field="profession" sortable body={(rowData) => emptyFieldTemplate(rowData.profession)} />
                                <Column header="Status" body={statusBodyTemplate} sortable field="status" />
                                <Column
                                    header="Actions"
                                    body={(rowData) => (
                                        <Dropdown
                                            isOpen={dropdownOpen === rowData.id}
                                            toggle={() => toggleDropdown(rowData.id)}
                                        >
                                            <DropdownToggle tag="button" className="btn btn-light">
                                                <MoreVertical size={20} />
                                            </DropdownToggle>
                                            <DropdownMenu>
                                                <DropdownItem onClick={() => editClient(rowData)}>
                                                    <Edit size={16} className="me-2" />
                                                    Edit
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() => {
                                                        setClientToDelete(rowData);
                                                        toggleDeleteModal();
                                                    }}
                                                    className="text-danger"
                                                >
                                                    <Trash size={16} className="me-2" />
                                                    Delete
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    )}
                                />
                            </DataTable>
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <Modal isOpen={modal} centered toggle={toggle} size="lg">
                <Form onSubmit={handleSubmit}>
                    <ModalHeader toggle={() => { toggle(); resetForm(); }}>{selectedClient ? 'Edit Client' : 'Add Client'}</ModalHeader>
                    <ModalBody>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Name*</Label>
                                    <Input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Email*</Label>
                                    <Input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Phone*</Label>
                                    <Input
                                        type="text"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>WhatsApp</Label>
                                    <Input
                                        type="text"
                                        value={whatsapp}
                                        onChange={(e) => setWhatsapp(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Company Name</Label>
                                    <Input
                                        type="text"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Profession</Label>
                                    <Input
                                        type="text"
                                        value={profession}
                                        onChange={(e) => setProfession(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* <Row>
                            
                        </Row> */}

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Country</Label>
                                    <Select
                                        options={countries.map(country => ({
                                            value: country.id,
                                            label: country.name
                                        }))}
                                        value={selectedCountry}
                                        onChange={(selectedOption) => {
                                            setSelectedCountry(selectedOption);
                                            setSelectedState(null);
                                            setSelectedCity(null);
                                            setCities([]);
                                            if (selectedOption) {
                                                fetchStates(selectedOption.value);
                                            } else {
                                                setStates([]);
                                            }
                                        }}
                                        isClearable
                                        placeholder="Select Country"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>State</Label>
                                    <Select
                                        options={states.map(state => ({
                                            value: state.id,
                                            label: state.name
                                        }))}
                                        value={selectedState}
                                        onChange={(selectedOption) => {
                                            setSelectedState(selectedOption);
                                            setSelectedCity(null);
                                            if (selectedOption) {
                                                fetchCities(selectedOption.value);
                                            } else {
                                                setCities([]);
                                            }
                                        }}
                                        isClearable
                                        placeholder="Select State"
                                        isDisabled={!selectedCountry}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>City</Label>
                                    <Select
                                        options={cities.map(city => ({
                                            value: city.id,
                                            label: city.name
                                        }))}
                                        value={selectedCity}
                                        onChange={(selectedOption) => {
                                            setSelectedCity(selectedOption);
                                        }}
                                        isClearable
                                        placeholder="Select City"
                                        isDisabled={!selectedState}
                                    />
                                </FormGroup>
                            </Col>

                        </Row>

                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Status</Label>
                                    <Input
                                        type="select"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <Label>Website</Label>
                                    <Input
                                        type="text"
                                        value={website}
                                        onChange={(e) => setWebsite(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>About client</Label>
                                    <Input
                                        type="text"
                                        value={client}
                                        onChange={(e) => setClient(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Address</Label>
                                    <Input
                                        type="textarea"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Remarks</Label>
                                    <Input
                                        type="textarea"
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Social media</Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={socialMedia}
                                        onChange={setSocial}
                                        style={{ height: '100px', marginBottom: '5px' }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* <Row>
                            
                        </Row> */}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => { toggle(); resetForm(); }}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={loading}>
                            {selectedClient ? "Update" : "Add"}
                            {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this client?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
                    <Button color="danger" onClick={handleDelete} disabled={loading}>
                        Delete
                        {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={exportModal} centered toggle={() => setExportModal(false)}>
                <ModalHeader toggle={() => setExportModal(false)}>Export Clients</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Export Format</Label>
                            <Input
                                type="select"
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                            >
                                <option value="excel">Excel (.xlsx)</option>
                                <option value="csv">CSV (.csv)</option>
                                <option value="pdf">PDF (.pdf)</option>
                            </Input>
                        </FormGroup>

                        <div className="mt-3 alert alert-info">
                            <small>
                                <strong>Note:</strong> The export will include all visible columns in the table.
                                For PDF exports, some columns may be abbreviated to fit the page.
                            </small>
                        </div>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setExportModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleExport}
                        disabled={exportLoading}
                    >
                        {exportLoading ? 'Exporting...' : 'Export'}
                        {exportLoading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={importModal} centered toggle={() => setImportModal(!importModal)}>
                <ModalHeader toggle={() => setImportModal(!importModal)}>Import Clients from Spreadsheet</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Select Excel/CSV File</Label>
                            <Input
                                type="file"
                                accept=".xlsx, .xls, .csv"
                                onChange={(e) => setFile(e.target.files[0])}
                            />
                            <small className="text-muted">
                                File should contain columns: Name, Email, Phone, Company, etc.
                            </small>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setImportModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleImport}
                        disabled={!file || importLoading}
                    >
                        {importLoading ? 'Importing...' : 'Import'}
                        {importLoading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>
            <Toast ref={toast} />
        </React.Fragment>
    );
};

export default ClientsList;